/**
 * MCQ (Multiple Choice Question) Element Schema
 * Supports multiple content types for question and choices
 */

import type {
    ElementSchema,
    TextFieldSchema,
    ListFieldSchema,
    GroupFieldSchema,
    BooleanFieldSchema,
} from '@ai-content-hub/content-kit';

export const defaultMcqElementI18n = {
    en: {
        question: 'Question',
        enterQuestionText: 'Enter question text',
        enterChoiceText: 'Enter choice text',
        explanation: 'Explanation',
        correctAnswer: 'Correct answer',
        explainCorrectAnswer: 'Explain the correct answer...',
        answerChoices: 'Answer Choices',
        choiceLabel: 'Choice',
        addChoice: 'Add Choice',
        multipleCorrectAnswers: 'Multiple Correct Answers',
        shuffleChoicesEditor: 'Shuffle Choices',
        mcqSelectCorrectAnswer: 'Select at least one correct answer.',
        mcqPlayerNoData: 'No question data',
        mcqPlayerNoChoices: 'No choices available',
        mcqPlayerCorrectIndicator: '← Correct',
    },
    vi: {
        question: 'Câu hỏi',
        enterQuestionText: 'Nhập nội dung câu hỏi',
        enterChoiceText: 'Nhập nội dung lựa chọn',
        explanation: 'Giải thích',
        correctAnswer: 'Đáp án đúng',
        explainCorrectAnswer: 'Giải thích đáp án đúng...',
        answerChoices: 'Các lựa chọn trả lời',
        choiceLabel: 'Lựa chọn',
        addChoice: 'Thêm lựa chọn',
        multipleCorrectAnswers: 'Nhiều đáp án đúng',
        shuffleChoicesEditor: 'Xáo trộn các lựa chọn',
        mcqSelectCorrectAnswer: 'Phải tích chọn ít nhất một đáp án đúng.',
        mcqPlayerNoData: 'Không có dữ liệu câu hỏi',
        mcqPlayerNoChoices: 'Không có lựa chọn nào',
        mcqPlayerCorrectIndicator: '← Đúng',
    },
};

// Re-export element block types for MCQ
import type {
    BlockContentType,
    BlockContent,
    TextBlockContent,
    ImageBlockContent,
    AudioBlockContent,
    VideoBlockContent,
    RichTextBlockContent,
} from '../block';

import {
    getBlockPreview,
    createDefaultBlockContent,
    getDisplayBlockContent,
    getPlainText,
} from '../block';

// Re-export helpers
export { getBlockPreview, createDefaultBlockContent, getDisplayBlockContent, getPlainText };

/**
 * MCQ uses BlockContentType for content types
 */
export type MCQContentType = BlockContentType;

/**
 * MCQ uses BlockContent for content data
 */
export type MCQContent = BlockContent;

// Re-export specific content types for backward compatibility
export type MCQTextContent = TextBlockContent;
export type MCQImageContent = ImageBlockContent;
export type MCQAudioContent = AudioBlockContent;
export type MCQVideoContent = VideoBlockContent;
export type MCQRichTextContent = RichTextBlockContent;

/**
 * MCQ Choice with flexible content type
 */
export interface MCQChoice {
    id: string;
    /** Rich content for the choice */
    content: MCQContent;
    isCorrect: boolean;
    feedback?: string;
}

/**
 * MCQ Element Data with flexible content types
 */
export interface MCQElementData {
    /** Question content (supports text, image, audio, video, rich-text) */
    question: MCQContent;
    choices: MCQChoice[];
    explanation?: string;
    multipleCorrect: boolean;
    shuffleChoices: boolean;
}

export type MCQAnswer = string | string[]; // Single choice ID or array for multiple correct

/**
 * Aliases for element block helpers (for backward compatibility)
 */
export const getContentPreview = getBlockPreview;
export const createDefaultContent = createDefaultBlockContent;
export const getDisplayContent = getDisplayBlockContent;

/**
 * Helper to get plain text from question for display/grading
 */
export function getQuestionText(data: MCQElementData): string {
    return getPlainText(data.question);
}

/**
 * Helper to get plain text from choice for display/grading
 */
export function getChoiceText(choice: MCQChoice): string {
    return getPlainText(choice.content);
}

/**
 * Helper to normalize MCQ data - ensures content has all required fields
 */
export function normalizeMCQData(data: Partial<MCQElementData>): MCQElementData {
    // Handle backward compatibility: if old 'question' string exists, convert to BlockContent
    // Also handle if data.question is already a BlockContent object
    let question: MCQContent;
    
    if (data.question && typeof data.question === 'object' && 'type' in data.question) {
        // Already a BlockContent object
        question = data.question;
    } else {
        // Either undefined or legacy string (from old schema)
        const legacyQuestion = typeof data.question === 'string' ? data.question : '';
        question = {
            type: 'text' as const,
            text: legacyQuestion,
            variant: 'body' as const,
            alignment: 'left' as const,
        };
    }

    // Normalize choices with full content defaults
    const choices: MCQChoice[] = (data.choices || []).map((choice) => {
        let content: MCQContent;
        
        if (choice.content && typeof choice.content === 'object' && 'type' in choice.content) {
            // Already a BlockContent object
            content = choice.content;
        } else {
            // Handle backward compatibility: if old 'label' exists, use it
            const legacyLabel = (choice as unknown as { label?: string }).label || '';
            content = {
                type: 'text' as const,
                text: legacyLabel,
                variant: 'body' as const,
                alignment: 'left' as const,
            };
        }
        
        return {
            id: choice.id,
            content,
            isCorrect: choice.isCorrect ?? false,
            feedback: choice.feedback,
        };
    });

    return {
        question,
        choices,
        explanation: data.explanation || '',
        multipleCorrect: data.multipleCorrect ?? false,
        shuffleChoices: data.shuffleChoices ?? false,
    };
}

export const mcqElementSchema: ElementSchema<MCQElementData> = {
    type: 'element-mcq',
    title: 'Multiple Choice Question',
    description:
        'A question with multiple choice options supporting text, image, audio, video, and rich text',
    version: '2.0.0',
    category: 'question',
    icon: 'CheckCircle',
    gradable: true,
    tags: ['mcq', 'quiz', 'question', 'assessment', 'multiple-choice'],
    fields: [
        // Note: The actual editing UI is custom-built in MCQEdit.tsx
        // These fields are here for schema documentation
        {
            name: 'question',
            type: 'group',
            label: 'Question',
            description: 'The question content (supports text, image, audio, video, rich-text)',
            required: true,
        } as GroupFieldSchema,
        {
            name: 'choices',
            type: 'list',
            label: 'Answer Choices',
            description: 'Add answer choices (mark correct ones)',
            required: true,
            minItems: 2,
            maxItems: 8,
            sortable: true,
        } as ListFieldSchema,
        {
            name: 'explanation',
            type: 'text',
            label: 'Explanation',
            description: 'Explanation shown after answer',
            placeholder: 'Explain the correct answer...',
            multiline: true,
        } as TextFieldSchema,
        {
            name: 'multipleCorrect',
            type: 'boolean',
            label: 'Multiple Correct Answers',
            description: 'Allow selecting multiple choices',
            defaultValue: false,
        } as BooleanFieldSchema,
        {
            name: 'shuffleChoices',
            type: 'boolean',
            label: 'Shuffle Choices',
            description: 'Randomize choice order on each attempt',
            // EAA-2030: new questions shuffle by default so the key is not always "A".
            defaultValue: true,
        } as BooleanFieldSchema,
    ],
    defaultData: {
        question: { type: 'text', text: '', variant: 'body', alignment: 'left' },
        choices: [
            {
                id: 'a',
                content: { type: 'text', text: '', variant: 'body', alignment: 'left' },
                isCorrect: false,
            },
            {
                id: 'b',
                content: { type: 'text', text: '', variant: 'body', alignment: 'left' },
                isCorrect: false,
            },
        ],
        explanation: '',
        multipleCorrect: false,
        shuffleChoices: true,
    },
    i18n: defaultMcqElementI18n,
};
